/**
 * @file BenefitPlanDetailsTab.js
 * @description Tab component for managing detailed benefit plan information.
 */

import {
    useState,
    useEffect,
    useRef,
    useContext,
    forwardRef,
    useImperativeHandle,
} from 'react';
import { CharacterLimitIndicator } from '../../helpers/CharacterLimitIndicator';
import PlanYearDropdown from '../../shared/asyncDropdowns/PlanYearDropdown';
import RichTextEditor from '../../new-portal-page/RichTextEditor';
import style from '../PlanYear.module.scss';
import { ModalContext, ModalTypes } from '../../../store/ModalContext';
import { searchPlanYears } from '../../../services/planYearService';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

export const VALUE_TYPE = {
    Currency: 1,
    Percent: 2,
    Alphanumeric: 3,
};

const VALUE_TYPE_SYMBOLS = {
    [VALUE_TYPE.Currency]: '$',
    [VALUE_TYPE.Percent]: '%',
    [VALUE_TYPE.Alphanumeric]: 'Aa',
};

const SYMBOL_TO_VALUE_TYPE = {
    $: VALUE_TYPE.Currency,
    '%': VALUE_TYPE.Percent,
    Aa: VALUE_TYPE.Alphanumeric,
};

const BenefitPlanDetailsTab = forwardRef(
    ({ data, onChange, clearTrigger, onSave }, ref) => {
        const { showModal } = useContext(ModalContext);

        const [planYears, setPlanYears] = useState([]);
        const [selectedPlanYearId, setSelectedPlanYearId] = useState(null);
        const [planDetails, setPlanDetails] = useState([]);
        const [errors, setErrors] = useState({});
        const [showCustomTextEditor, setShowCustomTextEditor] = useState({});
        const [valueTypes, setValueTypes] = useState({});

        const [initialContentMap, setInitialContentMap] = useState({});
        const [editLabelsMode, setEditLabelsMode] = useState(false);

        const prevClearTriggerRef = useRef(clearTrigger);

        const tenantId = data?.tenantId;

        const showOutOfNetwork = [1, 2, 3].includes(data.planTypeId);

        const isMedicalDentalVisionPlan = [1, 2, 3].includes(data.planTypeId);

        const hasPlanDetails = data.planDetails && data.planDetails.length > 0;

        const [customDetailText, setCustomDetailText] = useState('');

        const [customInitialContentMap, setCustomInitialContentMap] = useState(
            {},
        );

        useImperativeHandle(ref, () => ({
            validateAllDetails: (showToast = true) => {
                let hasErrors = false;
                const newErrors = {};

                planDetails.forEach((detail, index) => {
                    const valueTypeId =
                        valueTypes[`${index}_row`] ?? VALUE_TYPE.Alphanumeric;

                    ['inNetworkValue', 'outNetworkValue'].forEach(field => {
                        const val = detail[field] || '';
                        const errorMessage = validateInput(val, valueTypeId);

                        if (errorMessage) {
                            hasErrors = true;
                            newErrors[`${index}_${field}`] = errorMessage;
                        } else {
                            newErrors[`${index}_${field}`] = '';
                        }
                    });
                });

                setErrors(newErrors);

                if (hasErrors && showToast) {
                    Toast.error(getText('msg_43e7e95e8f7c'));
                }

                return !hasErrors;
            },
        }));

        useEffect(() => {
            if (!data.planDetailCategories || !planYears.length) {
                setCustomInitialContentMap({});
                return;
            }

            if (
                !customInitialContentMap ||
                Object.keys(customInitialContentMap).length === 0
            ) {
                const map = {};
                data.planDetailCategories.forEach(category => {
                    if (category.systemPlanDetailCategoryId === 22) {
                        map[category.planYearId] = category.text || '';
                    }
                });

                setCustomInitialContentMap(map);
            }
        }, [data.planDetailCategories, planYears]);

        useEffect(() => {
            if (!data.planDetailCategories || !planYears.length) {
                setCustomInitialContentMap({});
                return;
            }

            const map = {};
            data.planDetailCategories.forEach(category => {
                if (category.systemPlanDetailCategoryId === 22) {
                    map[category.planYearId] = category.text || '';
                }
            });

            setCustomInitialContentMap(map);
        }, [selectedPlanYearId]);

        useEffect(() => {
            setShowCustomTextEditor({});
        }, [selectedPlanYearId]);

        useEffect(() => {
            if (!selectedPlanYearId) {
                setCustomDetailText('');
                return;
            }

            if (!isMedicalDentalVisionPlan) {
                setCustomDetailText(
                    customInitialContentMap[selectedPlanYearId] || '',
                );
            }
        }, [
            selectedPlanYearId,
            customInitialContentMap,
            isMedicalDentalVisionPlan,
            hasPlanDetails,
        ]);

        useEffect(() => {
            if (!tenantId) {
                setPlanYears([]);
                setSelectedPlanYearId(null);
                return;
            }

            const fetchPlanYears = async () => {
                try {
                    const response = await searchPlanYears(
                        tenantId,
                        1,
                        1000,
                        '',
                        'name',
                        'asc',
                    );
                    setPlanYears(response.viewModels ?? []);
                } catch (error) {
                    console.error('Failed to fetch plan years:', error);
                    setPlanYears([]);
                }
            };

            fetchPlanYears();
        }, [tenantId]);

        const formatValueForDisplay = (rawValue, valueTypeId) => {
            if (rawValue == null) return '';
            return rawValue;
        };

        const validateInput = (inputValue, valueTypeId) => {
            if (!inputValue) return '';

            const val = inputValue.trim();

            switch (valueTypeId) {
                case VALUE_TYPE.Currency: {
                    // Allow optional leading $, digits, commas, optional decimal point with unlimited decimals
                    const currencyRegex =
                        /^\$?\d{1,3}(,\d{3})*(\.\d+)?$|^\$?\d+(\.\d+)?$/;
                    if (!currencyRegex.test(val)) {
                        return 'Currency must be a valid number with optional commas and decimal point';
                    }
                    const numericPart = val
                        .replace(/^\$/, '')
                        .replace(/,/g, '');
                    const num = Number(numericPart);
                    if (isNaN(num)) {
                        return 'Currency must be a valid number';
                    }
                    break;
                }
                case VALUE_TYPE.Percent: {
                    // Allow digits, optional decimal point with up to two decimals, optional trailing %
                    const percentRegex = /^\d+(\.\d{1,2})?%?$/;
                    if (!percentRegex.test(val)) {
                        return 'Percent must be a valid number with up to two decimal places';
                    }
                    const numericPart = val.replace(/%$/, '').replace(/,/g, '');
                    const num = Number(numericPart);
                    if (isNaN(num) || num < 0 || num > 100) {
                        return 'Percent must be between 0 and 100';
                    }
                    break;
                }
                case VALUE_TYPE.Alphanumeric:
                default:
                    break;
            }
            return '';
        };

        const valueTypesInitializedForYear = useRef(null);

        useEffect(() => {
            if (!selectedPlanYearId || !data.planDetails) {
                setPlanDetails([]);
                setValueTypes({});
                valueTypesInitializedForYear.current = null;
                return;
            }

            if (valueTypesInitializedForYear.current === selectedPlanYearId) {
                const filteredDetails = data.planDetails.filter(
                    detail => detail.planYearId === selectedPlanYearId,
                );
                setPlanDetails(filteredDetails);
                return;
            }

            const filteredDetails = data.planDetails.filter(
                detail => detail.planYearId === selectedPlanYearId,
            );
            setPlanDetails(filteredDetails);

            const initialValueTypes = {};
            filteredDetails.forEach((detail, index) => {
                const typeId = [
                    VALUE_TYPE.Currency,
                    VALUE_TYPE.Percent,
                    VALUE_TYPE.Alphanumeric,
                ].includes(detail.planDetailsValueTypeId)
                    ? detail.planDetailsValueTypeId
                    : VALUE_TYPE.Alphanumeric;
                initialValueTypes[`${index}_row`] = typeId;
            });
            setValueTypes(initialValueTypes);

            valueTypesInitializedForYear.current = selectedPlanYearId;
        }, [selectedPlanYearId, data.planDetails]);

        useEffect(() => {
            if (clearTrigger === undefined || clearTrigger === null) return;

            if (clearTrigger !== prevClearTriggerRef.current) {
                prevClearTriggerRef.current = clearTrigger;

                if (!selectedPlanYearId) return;

                showModal({
                    type: ModalTypes.ACTION,
                    title: getText('msg_b78130c92f32'),
                    subtitle: getText('msg_05963f20a4c6'),
                    text: getText('msg_3152238ebcac'),
                    yesButtonText: getText('msg_a6d425439711'),
                    noButtonText: getText('msg_77dfd2135f4d'),
                    onYes: () => {
                        const clearedPlanDetails = data.planDetails.map(
                            detail => {
                                if (detail.planYearId === selectedPlanYearId) {
                                    return {
                                        ...detail,
                                        inNetworkValue: '',
                                        outNetworkValue: '',
                                    };
                                }
                                return detail;
                            },
                        );

                        const clearedCategories = data.planDetailCategories.map(
                            category => {
                                if (
                                    category.planYearId === selectedPlanYearId
                                ) {
                                    return {
                                        ...category,
                                        text: '',
                                    };
                                }
                                return category;
                            },
                        );

                        onChange({
                            ...data,
                            planDetails: clearedPlanDetails,
                            planDetailCategories: clearedCategories,
                        });

                        setPlanDetails(
                            clearedPlanDetails.filter(
                                detail =>
                                    detail.planYearId === selectedPlanYearId,
                            ),
                        );

                        setShowCustomTextEditor({});
                        setErrors({});
                        setValueTypes({});
                        setInitialContentMap({});
                    },
                    onNo: () => {},
                });
            }
        }, [clearTrigger, selectedPlanYearId, data, onChange, showModal]);

        const handlePlanYearChange = planYearId => {
            const isValid = ref?.current?.validateAllDetails?.(false);
            if (!isValid) {
                Toast.error(getText('msg_e2d2ab0aebc2'));
                return;
            }

            setSelectedPlanYearId(planYearId);
            setErrors({});
            setValueTypes({});
            setInitialContentMap({});
            valueTypesInitializedForYear.current = null;
            setEditLabelsMode(false);
            setShowCustomTextEditor({});
        };

        const handleDetailChange = (detailIndex, field, inputValue) => {
            const updatedDetails = planDetails.map((detail, index) => {
                if (index === detailIndex) {
                    return {
                        ...detail,
                        [field]: inputValue,
                    };
                }
                return detail;
            });
            setPlanDetails(updatedDetails);

            const updatedPlanDetails = data.planDetails.map(detail => {
                if (
                    detail.planYearId === selectedPlanYearId &&
                    detail.systemPlanDetailId ===
                        planDetails[detailIndex].systemPlanDetailId
                ) {
                    return {
                        ...detail,
                        [field]: inputValue,
                    };
                }
                return detail;
            });
            onChange({
                ...data,
                planDetails: updatedPlanDetails,
            });

            setErrors(prevErrors => ({
                ...prevErrors,
                [`${detailIndex}_${field}`]: '',
            }));
        };

        const handleDetailBlur = (detailIndex, field) => {
            const value = planDetails[detailIndex]?.[field] || '';
            const valueTypeId =
                valueTypes[`${detailIndex}_row`] ?? VALUE_TYPE.Alphanumeric;

            const errorMessage = validateInput(value, valueTypeId);

            setErrors(prevErrors => ({
                ...prevErrors,
                [`${detailIndex}_${field}`]: errorMessage,
            }));
        };

        const handleValueTypeChange = (detailIndex, newTypeSymbol) => {
            const newTypeId = SYMBOL_TO_VALUE_TYPE[newTypeSymbol];
            if (newTypeId === undefined) return;

            setValueTypes(prev => ({
                ...prev,
                [`${detailIndex}_row`]: newTypeId,
            }));

            const updatedPlanDetails = planDetails.map((detail, index) => {
                if (index === detailIndex) {
                    return {
                        ...detail,
                        planDetailsValueTypeId: newTypeId,
                    };
                }
                return detail;
            });
            setPlanDetails(updatedPlanDetails);

            const updatedDataPlanDetails = data.planDetails.map(detail => {
                if (
                    detail.planYearId === selectedPlanYearId &&
                    detail.systemPlanDetailId ===
                        planDetails[detailIndex].systemPlanDetailId
                ) {
                    return {
                        ...detail,
                        planDetailsValueTypeId: newTypeId,
                    };
                }
                return detail;
            });

            onChange({
                ...data,
                planDetails: updatedDataPlanDetails,
            });

            ['inNetworkValue', 'outNetworkValue'].forEach(field => {
                const val = planDetails[detailIndex][field] || '';
                const errorMessage = validateInput(val, newTypeId);
                setErrors(prevErrors => ({
                    ...prevErrors,
                    [`${detailIndex}_${field}`]: errorMessage,
                }));
            });
        };

        const sanitizeValue = val => {
            if (val == null || val === '') return null;
            const cleaned = val
                .toString()
                .replace(/[$%]/g, '')
                .replace(/,/g, '')
                .trim();
            if (cleaned === '') return null;
            const num = Number(cleaned);
            return isNaN(num) ? null : num;
        };

        const handleSave = () => {
            if (!selectedPlanYearId) {
                onSave(data);
                return;
            }

            const sanitizedPlanDetails = data.planDetails.map(detail => {
                if (detail.planYearId === selectedPlanYearId) {
                    return {
                        ...detail,
                        inNetworkValue: sanitizeValue(detail.inNetworkValue),
                        outNetworkValue: sanitizeValue(detail.outNetworkValue),
                    };
                }
                return detail;
            });

            const sanitizedData = {
                ...data,
                planDetails: sanitizedPlanDetails,
            };

            onSave(sanitizedData);
        };

        const hasActualContent = html => {
            if (!html) return false;

            const str = html.replace(/&nbsp;/gi, ' ').trim();

            const onlyEmpty =
                /^(?:\s|<br\s*\/?>|<\/?\s*[a-zA-Z0-9-]+\s*\/?>)*$/.test(str);

            return !onlyEmpty;
        };

        const handleCustomTextToggle = categoryId => {
            setShowCustomTextEditor(prev => {
                const isShown = !!prev[categoryId];

                if (!isShown) {
                    const currentText = getCategoryText(categoryId);
                    setInitialContentMap(prev => ({
                        ...prev,
                        [categoryId]: currentText,
                    }));
                } else {
                    const currentText = getCategoryText(categoryId);
                    if (!hasActualContent(currentText)) {
                        const updatedCategories = data.planDetailCategories.map(
                            category => {
                                if (
                                    category.systemPlanDetailCategoryId ===
                                        categoryId &&
                                    category.planYearId === selectedPlanYearId
                                ) {
                                    return { ...category, text: '' };
                                }
                                return category;
                            },
                        );
                        onChange({
                            ...data,
                            planDetailCategories: updatedCategories,
                        });

                        setInitialContentMap(prev => {
                            const copy = { ...prev };
                            delete copy[categoryId];
                            return copy;
                        });
                    }
                }

                return { ...prev, [categoryId]: !isShown };
            });
        };

        const handleCustomTextUpdate = (categoryId, content) => {
            const updatedCategories = data.planDetailCategories.map(
                category => {
                    if (
                        category.systemPlanDetailCategoryId === categoryId &&
                        category.planYearId === selectedPlanYearId
                    ) {
                        return { ...category, text: content };
                    }
                    return category;
                },
            );

            onChange({ ...data, planDetailCategories: updatedCategories });
        };

        const getCategoryText = categoryId => {
            const category = data.planDetailCategories?.find(
                cat =>
                    cat.systemPlanDetailCategoryId === categoryId &&
                    cat.planYearId === selectedPlanYearId,
            );
            return category?.text || '';
        };

        const getGroupedDetails = () => {
            if (!data.planDetailCategories) return [];

            const categoryOrder = data.planDetailCategories
                .filter(category => category.planYearId === selectedPlanYearId)
                .filter(
                    category =>
                        !(
                            hasPlanDetails &&
                            category.systemPlanDetailCategoryId === 22
                        ),
                )
                .sort((a, b) => a.sortOrder - b.sortOrder);

            const groupedDetails = [];

            categoryOrder.forEach(category => {
                const detailsInCategory = planDetails.filter(
                    detail =>
                        detail.systemPlanDetail.systemPlanDetailCategoryId ===
                        category.systemPlanDetailCategoryId,
                );

                const categoryName =
                    detailsInCategory.length > 0
                        ? detailsInCategory[0].systemPlanDetail
                              .systemPlanDetailCategory?.name
                        : `Category ${category.systemPlanDetailCategoryId}`;

                groupedDetails.push({
                    categoryId: category.systemPlanDetailCategoryId,
                    categoryName: categoryName || 'Unknown Category',
                    details: detailsInCategory,
                });
            });

            return groupedDetails;
        };

        const getFieldClasses = (detailIndex, fieldName) => {
            const errorKey = `${detailIndex}_${fieldName}`;
            const hasError = !!errors[errorKey];
            let classes = 'form-control';
            if (hasError) {
                classes += ' is-invalid';
            }
            return classes;
        };

        const groupedDetails = getGroupedDetails();

        const toggleEditLabelsMode = () => {
            setEditLabelsMode(prev => !prev);
        };

        const handleLabelChange = (systemPlanDetailId, value) => {
            const updatedLocalDetails = planDetails.map(detail => {
                if (detail.systemPlanDetailId === systemPlanDetailId) {
                    return {
                        ...detail,
                        name: value,
                    };
                }
                return detail;
            });
            setPlanDetails(updatedLocalDetails);

            const updatedPlanDetails = data.planDetails.map(detail => {
                if (
                    detail.planYearId === selectedPlanYearId &&
                    detail.systemPlanDetailId === systemPlanDetailId
                ) {
                    return {
                        ...detail,
                        name: value,
                    };
                }
                return detail;
            });

            onChange({
                ...data,
                planDetails: updatedPlanDetails,
            });
        };

        const renderInputWithSymbol = (detailIndex, field, rawValue, error) => {
            const inputId = `${field}_${detailIndex}`;
            const isError = !!error;

            const valueTypeId =
                valueTypes[`${detailIndex}_row`] ?? VALUE_TYPE.Alphanumeric;

            const displayValue = formatValueForDisplay(
                rawValue || '',
                valueTypeId,
            );

            return (
                <div>
                    <input
                        type="text"
                        id={inputId}
                        className={getFieldClasses(detailIndex, field)}
                        value={displayValue}
                        onChange={e =>
                            handleDetailChange(
                                detailIndex,
                                field,
                                e.target.value,
                            )
                        }
                        onBlur={() => handleDetailBlur(detailIndex, field)}
                        placeholder={`Enter ${
                            field === 'inNetworkValue'
                                ? 'in network'
                                : 'out of network'
                        } value`}
                        maxLength={128}
                        disabled={editLabelsMode}
                    />
                    <CharacterLimitIndicator
                        inputStr={displayValue}
                        maxChars={128}
                    />
                    {isError && <div className="invalid-feedback">{error}</div>}
                </div>
            );
        };

        const selectedPlanYearObj = planYears.find(
            py => py.id === selectedPlanYearId,
        );

        const selectedPlanYearIndex = planYears.findIndex(
            py => py.id === selectedPlanYearId,
        );
        const priorPlanYearObj =
            selectedPlanYearIndex > 0
                ? planYears[selectedPlanYearIndex - 1]
                : null;

        const isSelectedPlanYearPopulated = () => {
            if (!selectedPlanYearId) return false;

            const detailsForSelectedYear = data.planDetails.filter(
                detail => detail.planYearId === selectedPlanYearId,
            );

            if (detailsForSelectedYear.length === 0) return false;

            return detailsForSelectedYear.some(detail => {
                const inVal = detail.inNetworkValue;
                const outVal = detail.outNetworkValue;
                return (
                    (inVal !== null && inVal !== '') ||
                    (outVal !== null && outVal !== '')
                );
            });
        };

        const showCopyPriorYearButton =
            selectedPlanYearId &&
            priorPlanYearObj &&
            !isSelectedPlanYearPopulated();

        const handleCopyPriorYear = () => {
            if (!priorPlanYearObj || !selectedPlanYearObj) {
                return;
            }

            const priorYearDetails = data.planDetails.filter(
                detail => detail.planYearId === priorPlanYearObj.id,
            );

            const selectedYearDetails = data.planDetails.filter(
                detail => detail.planYearId === selectedPlanYearObj.id,
            );

            const updatedSelectedYearDetails = selectedYearDetails.map(
                detail => {
                    const priorDetail = priorYearDetails.find(
                        pd =>
                            pd.systemPlanDetail.systemPlanDetailCategoryId ===
                                detail.systemPlanDetail
                                    .systemPlanDetailCategoryId &&
                            pd.systemPlanDetailId === detail.systemPlanDetailId,
                    );

                    if (priorDetail) {
                        return {
                            ...detail,
                            inNetworkValue: priorDetail.inNetworkValue,
                            outNetworkValue: priorDetail.outNetworkValue,
                            planDetailsValueTypeId:
                                priorDetail.planDetailsValueTypeId,
                            name: priorDetail.name,
                        };
                    }
                    return detail;
                },
            );

            const priorYearCategories = data.planDetailCategories.filter(
                category => category.planYearId === priorPlanYearObj.id,
            );

            const selectedYearCategories = data.planDetailCategories.filter(
                category => category.planYearId === selectedPlanYearObj.id,
            );

            const priorCategoryMap = {};
            priorYearCategories.forEach(cat => {
                priorCategoryMap[cat.systemPlanDetailCategoryId] =
                    cat.text || '';
            });

            const updatedSelectedYearCategories = selectedYearCategories.map(
                cat => {
                    const copiedText =
                        priorCategoryMap[cat.systemPlanDetailCategoryId] || '';
                    return {
                        ...cat,
                        text: copiedText,
                    };
                },
            );

            const priorCustomCat = priorYearCategories.find(
                c => c.systemPlanDetailCategoryId === 22,
            );
            const selectedHasCustomCat = selectedYearCategories.some(
                c => c.systemPlanDetailCategoryId === 22,
            );

            if (priorCustomCat && !selectedHasCustomCat) {
                updatedSelectedYearCategories.push({
                    planId: data.id,
                    planYearId: selectedPlanYearObj.id,
                    systemPlanDetailCategoryId: 22,
                    sortOrder: priorCustomCat.sortOrder ?? 0,
                    text: priorCustomCat.text || '',
                });
            }

            const allValuesEmpty = updatedSelectedYearDetails.every(detail => {
                const inVal = detail.inNetworkValue;
                const outVal = detail.outNetworkValue;
                return (
                    (inVal === null || inVal === undefined || inVal === '') &&
                    (outVal === null || outVal === undefined || outVal === '')
                );
            });

            if (allValuesEmpty) {
                Toast.info(getText('msg_3f2625201b87'));
            }

            const updatedPlanDetails = [
                ...data.planDetails.filter(
                    detail => detail.planYearId !== selectedPlanYearObj.id,
                ),
                ...updatedSelectedYearDetails,
            ];

            const updatedPlanDetailCategories = [
                ...data.planDetailCategories.filter(
                    category => category.planYearId !== selectedPlanYearObj.id,
                ),
                ...updatedSelectedYearCategories,
            ];

            const newValueTypes = {};
            updatedSelectedYearDetails.forEach((detail, index) => {
                const typeId = [
                    VALUE_TYPE.Currency,
                    VALUE_TYPE.Percent,
                    VALUE_TYPE.Alphanumeric,
                ].includes(detail.planDetailsValueTypeId)
                    ? detail.planDetailsValueTypeId
                    : VALUE_TYPE.Alphanumeric;
                newValueTypes[`${index}_row`] = typeId;
            });

            setPlanDetails(updatedSelectedYearDetails);
            setValueTypes(newValueTypes);
            setErrors({});
            setShowCustomTextEditor({});
            setInitialContentMap(
                updatedSelectedYearCategories.reduce((acc, cat) => {
                    acc[cat.systemPlanDetailCategoryId] = cat.text || '';
                    return acc;
                }, {}),
            );

            const copiedCustomText =
                priorCategoryMap[22] !== undefined
                    ? priorCategoryMap[22]
                    : customInitialContentMap[selectedPlanYearObj.id] || '';

            setCustomInitialContentMap(prev => ({
                ...prev,
                [selectedPlanYearObj.id]: copiedCustomText || '',
            }));

            if (!isMedicalDentalVisionPlan) {
                setCustomDetailText(copiedCustomText || '');
            }

            onChange({
                ...data,
                planDetails: updatedPlanDetails,
                planDetailCategories: updatedPlanDetailCategories,
            });
        };

        const handleCustomDetailTextChange = content => {
            setCustomDetailText(content);

            const updatedCategories = data.planDetailCategories
                ? [...data.planDetailCategories]
                : [];

            const index = updatedCategories.findIndex(
                cat =>
                    cat.systemPlanDetailCategoryId === 22 &&
                    cat.planYearId === selectedPlanYearId,
            );

            if (index >= 0) {
                updatedCategories[index] = {
                    ...updatedCategories[index],
                    text: content,
                };
            } else {
                updatedCategories.push({
                    planId: data.id,
                    planYearId: selectedPlanYearId,
                    systemPlanDetailCategoryId: 22,
                    sortOrder: 0,
                    text: content,
                });
            }

            onChange({
                ...data,
                planDetailCategories: updatedCategories,
            });
        };

        return (
            <div className="col-12 col-xs-12 col-md-12 col-lg-10 m-auto pb-5">
                <div className="col-12 col-xs-12 col-md-12 col-lg-8 m-auto pb-5">
                    <div className="d-flex align-items-start gap-3">
                        <div className="flex-grow-1">
                            <PlanYearDropdown
                                selectedPlanYear={selectedPlanYearId}
                                onPlanYearChange={handlePlanYearChange}
                                inlineLabel={false}
                                returnFullObject={true}
                                planYears={planYears}
                            />
                        </div>
                        {showCopyPriorYearButton && hasPlanDetails && (
                            <button
                                type="button"
                                className={`button-base ing-button-primary-left px-5 ${style.actionButton}`}
                                style={{
                                    whiteSpace: 'nowrap',
                                    marginTop: '37px',
                                }}
                                onClick={handleCopyPriorYear}
                            >
                                {getText('msg_1c880eb97b75')}
                            </button>
                        )}
                    </div>
                </div>

                {!selectedPlanYearId && (
                    <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2" />
                        {getText('msg_e701afcc41f9')}
                    </div>
                )}

                {selectedPlanYearId && groupedDetails && (
                    <div className="mb-4 d-flex flex-direction-row align-items-center gap-3">
                        <h5 className="p-0 m-0">
                            {getText('msg_dc3decbb9384')}
                        </h5>
                        {hasPlanDetails && (
                            <button
                                type="button"
                                className={`btn p-0 m-0 ${style.editLabelsButton}`}
                                onClick={toggleEditLabelsMode}
                            >
                                {editLabelsMode
                                    ? 'Edit Details'
                                    : 'Edit Labels'}
                            </button>
                        )}
                    </div>
                )}
                {!isMedicalDentalVisionPlan && selectedPlanYearId && (
                    <div className="row mb-4">
                        <div className="col-12 col-md-2"></div>
                        <div className="col-12 col-md-8">
                            <RichTextEditor
                                initialContent={
                                    customInitialContentMap[
                                        selectedPlanYearId
                                    ] || ''
                                }
                                onContentUpdate={handleCustomDetailTextChange}
                                disableHomeLink={true}
                            />
                        </div>
                        <div className="col-12 col-md-2"></div>
                    </div>
                )}
                {!hasPlanDetails ? (
                    selectedPlanYearId &&
                    groupedDetails.length === 0 && (
                        <div className="alert alert-warning">
                            <i className="bi bi-exclamation-triangle me-2" />
                            {getText('msg_4926b1f53db3')}
                        </div>
                    )
                ) : (
                    <>
                        {selectedPlanYearId &&
                            groupedDetails.map(group => {
                                const customCategoryText = getCategoryText(
                                    group.categoryId,
                                );
                                const isEditorShown =
                                    !!showCustomTextEditor[group.categoryId];
                                const hasContent =
                                    hasActualContent(customCategoryText);

                                return (
                                    <div
                                        key={group.categoryId}
                                        className="mb-5"
                                    >
                                        <div className="row mb-3">
                                            <div className="col-12 col-md-2"></div>
                                            {showOutOfNetwork && (
                                                <div
                                                    className={
                                                        'col-12 col-md-4 text-center'
                                                    }
                                                >
                                                    <strong>
                                                        {getText(
                                                            'msg_e6e125cba048',
                                                        )}
                                                    </strong>
                                                </div>
                                            )}
                                            {showOutOfNetwork && (
                                                <div className="col-12 col-md-4 text-center">
                                                    <strong>
                                                        {getText(
                                                            'msg_7a43cdd08459',
                                                        )}
                                                    </strong>
                                                </div>
                                            )}
                                            <div className="col-12 col-md-2 text-center"></div>
                                        </div>

                                        <div className="row mb-2">
                                            <div className="col-12 col-md-2">
                                                <h6 className="mb-3">
                                                    {group.categoryName}
                                                </h6>
                                            </div>
                                            <div className="col-12 col-md-8">
                                                <hr className="mt-0 mb-3" />
                                            </div>
                                        </div>

                                        <div className="row mb-1">
                                            <div className="col-12 col-md-2"></div>
                                            <div className="col-12 col-md-4">
                                                <button
                                                    type="button"
                                                    className={`btn btn-link p-0 ${
                                                        isEditorShown
                                                            ? hasContent
                                                                ? 'text-success' // green if editor shown and has content
                                                                : 'text-primary' // blue if editor shown but empty
                                                            : hasContent
                                                              ? 'text-success' // green if not shown but has content
                                                              : 'text-primary' // blue if not shown and no content
                                                    }`}
                                                    onClick={() =>
                                                        handleCustomTextToggle(
                                                            group.categoryId,
                                                        )
                                                    }
                                                    style={{
                                                        whiteSpace: 'nowrap',
                                                        textDecoration: 'none',
                                                        marginLeft: '4px',
                                                    }}
                                                >
                                                    {isEditorShown
                                                        ? 'Hide Custom Text Editor'
                                                        : hasContent
                                                          ? 'View Custom Text'
                                                          : 'Add Custom Text'}
                                                </button>
                                            </div>
                                        </div>

                                        {isEditorShown && (
                                            <div className="row mb-4">
                                                <div className="col-12 col-md-2"></div>
                                                <div className="col-12 col-md-8">
                                                    <RichTextEditor
                                                        initialContent={
                                                            initialContentMap[
                                                                group.categoryId
                                                            ] || ''
                                                        }
                                                        onContentUpdate={content =>
                                                            handleCustomTextUpdate(
                                                                group.categoryId,
                                                                content,
                                                            )
                                                        }
                                                        disableHomeLink={true}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {group.details.length === 0 ? (
                                            <div className="row mb-4 mt-">
                                                <div className="col-12 col-md-2"></div>
                                                <div className="col-12 col-md-10">
                                                    <p className="text-muted font-italic">
                                                        {getText(
                                                            'msg_9acb387b374a',
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            group.details.map(
                                                (detail, detailIndex) => {
                                                    const globalIndex =
                                                        planDetails.findIndex(
                                                            pd => pd === detail,
                                                        );

                                                    const errorInNetwork =
                                                        errors[
                                                            `${globalIndex}_inNetworkValue`
                                                        ];
                                                    const errorOutNetwork =
                                                        errors[
                                                            `${globalIndex}_outNetworkValue`
                                                        ];

                                                    const rowValueType =
                                                        valueTypes[
                                                            `${globalIndex}_row`
                                                        ] ??
                                                        VALUE_TYPE.Alphanumeric;

                                                    return (
                                                        <div
                                                            key={
                                                                detail.systemPlanDetailId
                                                            }
                                                            className="row mb-4 mt-4 align-items-center"
                                                        >
                                                            <div className="col-12 col-md-2"></div>

                                                            <div
                                                                className={
                                                                    showOutOfNetwork
                                                                        ? 'd-flex flex-column col-12 col-md-4 pe-md-2 gap-2'
                                                                        : 'd-flex flex-column col-12 col-md-8 pe-md-2 gap-2'
                                                                }
                                                            >
                                                                {editLabelsMode ? (
                                                                    <div
                                                                        className="d-flex flex-direction-row align-items-center"
                                                                        style={{
                                                                            border: '1px solid blue',
                                                                            padding:
                                                                                '0 4px',
                                                                            margin: '-1px 0 -1px 0',
                                                                            minWidth:
                                                                                '100%',
                                                                        }}
                                                                    >
                                                                        <input
                                                                            type="text"
                                                                            className=""
                                                                            value={
                                                                                detail.name
                                                                            }
                                                                            onChange={e =>
                                                                                handleLabelChange(
                                                                                    detail.systemPlanDetailId,
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                            placeholder={
                                                                                detail
                                                                                    .systemPlanDetail
                                                                                    .name
                                                                            }
                                                                            maxLength={
                                                                                100
                                                                            }
                                                                            style={{
                                                                                font: 'inherit',
                                                                                color: 'inherit',
                                                                                outline:
                                                                                    'none',
                                                                                border: '0px',
                                                                                flexGrow: 1,
                                                                                minWidth: 0,
                                                                                textOverflow:
                                                                                    'ellipsis',
                                                                            }}
                                                                        />
                                                                        <i
                                                                            className="bi bi-arrow-repeat"
                                                                            style={{
                                                                                color: 'blue',
                                                                                width: 'fit-content',
                                                                                flexShrink: 0,
                                                                                marginLeft:
                                                                                    '8px',
                                                                                cursor: 'pointer',
                                                                            }}
                                                                            role="button"
                                                                            tabIndex={
                                                                                0
                                                                            }
                                                                            aria-label={getText(
                                                                                'msg_8b3816231374',
                                                                            )}
                                                                            onClick={() =>
                                                                                handleLabelChange(
                                                                                    detail.systemPlanDetailId,
                                                                                    '',
                                                                                )
                                                                            }
                                                                            onKeyDown={e => {
                                                                                if (
                                                                                    e.key ===
                                                                                        'Enter' ||
                                                                                    e.key ===
                                                                                        ' '
                                                                                ) {
                                                                                    e.preventDefault();
                                                                                    handleLabelChange(
                                                                                        detail.systemPlanDetailId,
                                                                                        '',
                                                                                    );
                                                                                }
                                                                            }}
                                                                        ></i>
                                                                    </div>
                                                                ) : (
                                                                    <label>
                                                                        {detail
                                                                            .name
                                                                            .length >
                                                                        0
                                                                            ? detail.name
                                                                            : detail
                                                                                  .systemPlanDetail
                                                                                  .name}
                                                                    </label>
                                                                )}
                                                                {renderInputWithSymbol(
                                                                    globalIndex,
                                                                    'inNetworkValue',
                                                                    detail.inNetworkValue,
                                                                    errorInNetwork,
                                                                )}
                                                            </div>

                                                            {showOutOfNetwork && (
                                                                <div className="d-flex flex-column col-12 col-md-4 ps-md-2 gap-2">
                                                                    {editLabelsMode ? (
                                                                        <div
                                                                            className="d-flex flex-direction-row align-items-center"
                                                                            style={{
                                                                                border: '1px solid blue',
                                                                                padding:
                                                                                    '0 4px',
                                                                                margin: '-1px 0 -1px 0',
                                                                                minWidth:
                                                                                    '100%',
                                                                            }}
                                                                        >
                                                                            <input
                                                                                type="text"
                                                                                className=""
                                                                                value={
                                                                                    detail.name
                                                                                }
                                                                                onChange={e =>
                                                                                    handleLabelChange(
                                                                                        detail.systemPlanDetailId,
                                                                                        e
                                                                                            .target
                                                                                            .value,
                                                                                    )
                                                                                }
                                                                                placeholder={
                                                                                    detail
                                                                                        .systemPlanDetail
                                                                                        .name
                                                                                }
                                                                                maxLength={
                                                                                    100
                                                                                }
                                                                                style={{
                                                                                    font: 'inherit',
                                                                                    color: 'inherit',
                                                                                    outline:
                                                                                        'none',
                                                                                    border: '0px',
                                                                                    flexGrow: 1,
                                                                                    minWidth: 0,
                                                                                    textOverflow:
                                                                                        'ellipsis',
                                                                                }}
                                                                            />
                                                                            <i
                                                                                className="bi bi-arrow-repeat"
                                                                                style={{
                                                                                    color: 'blue',
                                                                                    width: 'fit-content',
                                                                                    flexShrink: 0,
                                                                                    marginLeft:
                                                                                        '8px',
                                                                                    cursor: 'pointer',
                                                                                }}
                                                                                role="button"
                                                                                tabIndex={
                                                                                    0
                                                                                }
                                                                                aria-label={getText(
                                                                                    'msg_8b3816231374',
                                                                                )}
                                                                                onClick={() =>
                                                                                    handleLabelChange(
                                                                                        detail.systemPlanDetailId,
                                                                                        '',
                                                                                    )
                                                                                }
                                                                                onKeyDown={e => {
                                                                                    if (
                                                                                        e.key ===
                                                                                            'Enter' ||
                                                                                        e.key ===
                                                                                            ' '
                                                                                    ) {
                                                                                        e.preventDefault();
                                                                                        handleLabelChange(
                                                                                            detail.systemPlanDetailId,
                                                                                            '',
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            ></i>
                                                                        </div>
                                                                    ) : (
                                                                        <label>
                                                                            {detail
                                                                                .name
                                                                                .length >
                                                                            0
                                                                                ? detail.name
                                                                                : detail
                                                                                      .systemPlanDetail
                                                                                      .name}
                                                                        </label>
                                                                    )}
                                                                    {renderInputWithSymbol(
                                                                        globalIndex,
                                                                        'outNetworkValue',
                                                                        detail.outNetworkValue,
                                                                        errorOutNetwork,
                                                                    )}
                                                                </div>
                                                            )}

                                                            {Object.entries(
                                                                VALUE_TYPE_SYMBOLS,
                                                            ).map(
                                                                ([
                                                                    typeId,
                                                                    symbol,
                                                                ]) => (
                                                                    <div
                                                                        key={
                                                                            typeId
                                                                        }
                                                                        className={
                                                                            'd-flex flex-column col-12 pe-md-2 gap-4 align-self-start align-items-center w-auto'
                                                                        }
                                                                    >
                                                                        <label
                                                                            htmlFor={`rowValueType_${globalIndex}_${symbol}`}
                                                                            style={{
                                                                                fontWeight:
                                                                                    'bold',
                                                                                userSelect:
                                                                                    'none',
                                                                                width: 'fit-content',
                                                                            }}
                                                                        >
                                                                            {
                                                                                symbol
                                                                            }
                                                                        </label>

                                                                        <label
                                                                            htmlFor={`rowValueType_${globalIndex}_${symbol}`}
                                                                            className={`${style.radioButtonWrapper} ${
                                                                                editLabelsMode
                                                                                    ? style.disabled
                                                                                    : ''
                                                                            }`}
                                                                            style={{
                                                                                marginBottom: 0,
                                                                                width: 'fit-content',
                                                                            }}
                                                                        >
                                                                            <input
                                                                                type="radio"
                                                                                id={`rowValueType_${globalIndex}_${symbol}`}
                                                                                name={`rowValueType_${globalIndex}`}
                                                                                value={
                                                                                    symbol
                                                                                }
                                                                                checked={
                                                                                    Number(
                                                                                        typeId,
                                                                                    ) ===
                                                                                    rowValueType
                                                                                }
                                                                                onChange={() =>
                                                                                    handleValueTypeChange(
                                                                                        globalIndex,
                                                                                        symbol,
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    editLabelsMode
                                                                                }
                                                                            />
                                                                            <span
                                                                                className={
                                                                                    style.customRadio
                                                                                }
                                                                            ></span>
                                                                        </label>
                                                                    </div>
                                                                ),
                                                            )}
                                                        </div>
                                                    );
                                                },
                                            )
                                        )}
                                    </div>
                                );
                            })}
                    </>
                )}

                {selectedPlanYearId &&
                    hasPlanDetails &&
                    groupedDetails.length !== 0 && (
                        <div className="row pb-3">
                            <div className="col-12 col-xs-12 d-flex justify-content-end mt-4">
                                <button
                                    type="button"
                                    className={`button-base ing-button-primary-left align-self-center px-5 ${style.actionButton}`}
                                    onClick={handleSave}
                                >
                                    {getText('msg_efc007a393f6')}
                                </button>
                            </div>
                        </div>
                    )}
            </div>
        );
    },
);

export default BenefitPlanDetailsTab;
